import React, { useState, useEffect, useMemo, useRef } from 'react';
import Select from 'react-select';
import { db } from './firebase-config';
import { doc, onSnapshot } from 'firebase/firestore';
import { RefreshCw } from 'lucide-react';
import { eachDayOfInterval, format, startOfDay, parseISO, subDays } from 'date-fns';

import ComponentRatingChart from './components/ComponentRatingChart';
import TrainPerformanceChart from './components/TrainPerformanceChart';
import StatusDonutChart from './components/StatusDonutChart';
import FeedbackTrendChart from './components/FeedbackTrendChart';

import './App.css';

const getStartDate = (range) => {
  const today = startOfDay(new Date());
  if (range === 'last7days') return subDays(today, 7);
  if (range === 'last30days') return subDays(today, 30);
  if (range === 'last90days') return subDays(today, 90);
  return null;
};

const hasReportedRating = (train, component, averageKey) => {
  const count = train?.ratingCounts?.[component];
  if (typeof count === 'number') return count > 0;
  return Number(train?.[averageKey] || 0) > 0;
};

function App() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTrain, setSelectedTrain] = useState('');
  const [trainChartMode, setTrainChartMode] = useState('topRated');
  const [dateRange, setDateRange] = useState('last30days');
  const [selectedCoach, setSelectedCoach] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const updateTimeoutRef = useRef(null);

  useEffect(() => {
    const summaryDocRef = doc(db, 'dashboard', 'summary');
    const unsubscribe = onSnapshot(summaryDocRef, (doc) => {
      if (doc.exists()) {
        const rawData = doc.data();
        let parsedData = { ...rawData };
        if (typeof rawData.statsByTrain === 'string') {
          try {
            parsedData.statsByTrain = JSON.parse(rawData.statsByTrain);
          } catch (e) {
            console.error("Failed to parse statsByTrain JSON", e);
          }
        }
        if (typeof rawData.overallStats === 'string') {
          try {
            parsedData.overallStats = JSON.parse(rawData.overallStats);
          } catch (e) {
            console.error("Failed to parse overallStats JSON", e);
          }
        }
        setDashboardData(parsedData);
        setError(null);
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }
        setIsUpdating(true);
        updateTimeoutRef.current = setTimeout(() => {
          setIsUpdating(false);
        }, 1500);
      } else {
        setError("Summary document not found. Please run the aggregation script.");
      }
      setLoading(false);
    });
    return () => {
      unsubscribe();
      if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current);
    };
  }, []);

  const topTrainsData = useMemo(() => {
    if (!dashboardData || !dashboardData.statsByTrain || typeof dashboardData.statsByTrain !== 'object') {
      return { labels: [], datasets: [] };
    }
    const allTrains = Object.values(dashboardData.statsByTrain);
    const trainsWithOverallRatings = allTrains.filter(train =>
      hasReportedRating(train, 'generalCoach', 'avgGeneralRating')
    );
    let sortedTrains = [];
    if (trainChartMode === 'topRated') sortedTrains = [...trainsWithOverallRatings].sort((a, b) => (b.avgGeneralRating || 0) - (a.avgGeneralRating || 0));
    else if (trainChartMode === 'bottomRated') sortedTrains = [...trainsWithOverallRatings].sort((a, b) => (a.avgGeneralRating || 0) - (b.avgGeneralRating || 0));
    else sortedTrains = [...allTrains].sort((a, b) => (b.feedbackCount || 0) - (a.feedbackCount || 0));
    const top10Trains = sortedTrains.slice(0, 10);
    return {
      labels: top10Trains.map(train => `${(train.trainName || 'N/A').substring(0, 20)}...`),
      datasets: [{
        label: 'Average General Rating',
        data: top10Trains.map(train => train.avgGeneralRating || 0),
      }]
    };
  }, [dashboardData, trainChartMode]);

  const filteredTrendData = useMemo(() => {
    if (!dashboardData || !dashboardData.overallStats || typeof dashboardData.overallStats !== 'object') return {};
    const baseData = dashboardData.overallStats.feedbacksByDate || {};
    const dates = Object.keys(baseData).sort();
    if (dates.length === 0) return {};

    const endDate = startOfDay(new Date());
    let startDate = getStartDate(dateRange);
    
    // If 'allTime' or no range matched, use the earliest date in data
    if (!startDate) {
      startDate = parseISO(dates[0]);
    }

    try {
      const daysInterval = eachDayOfInterval({ start: startDate, end: endDate });
      const paddedData = {};
      daysInterval.forEach(day => {
        const dateStr = format(day, 'yyyy-MM-dd');
        paddedData[dateStr] = baseData[dateStr] || 0;
      });
      return paddedData;
    } catch (e) {
      console.error("Error generating dates", e);
      return baseData; // fallback
    }
  }, [dashboardData, dateRange]);

  const trainOptions = useMemo(() => {
    if (!dashboardData || !dashboardData.statsByTrain || typeof dashboardData.statsByTrain !== 'object') return [];
    return Object.keys(dashboardData.statsByTrain).map(trainNo => ({
      value: trainNo,
      label: `${dashboardData.statsByTrain[trainNo]?.trainName || 'N/A'} (${trainNo})`
    }));
  }, [dashboardData]);

  const coachOptions = useMemo(() => {
    if (!selectedTrain || !dashboardData || !dashboardData.statsByTrain || typeof dashboardData.statsByTrain !== 'object') return [];
    const trainData = dashboardData.statsByTrain[selectedTrain];
    if (!trainData || !trainData.coachStats) return [];
    return Object.keys(trainData.coachStats).map(coachNo => ({
      value: coachNo,
      label: `Coach ${coachNo}`
    }));
  }, [selectedTrain, dashboardData]);

  if (loading) return <div className="full-page-msg"><RefreshCw className="sync-icon" size={32} /><h1>Loading Dashboard...</h1></div>;
  if (error) return <div className="full-page-msg"><h1>Error Loading Data</h1><p>{error}</p></div>;

  const selectedTrainData = (dashboardData && dashboardData.statsByTrain && typeof dashboardData.statsByTrain === 'object') ? dashboardData.statsByTrain[selectedTrain] : null;
  const selectedCoachData = selectedTrainData?.coachStats?.[selectedCoach];

  const handleTrainChange = (selectedOption) => {
    const newTrain = selectedOption ? selectedOption.value : '';
    setSelectedTrain(newTrain);
    setSelectedCoach('');
  };

  const handleCoachChange = (selectedOption) => {
    setSelectedCoach(selectedOption ? selectedOption.value : '');
  };

  const dataForDetailView = selectedCoachData || selectedTrainData;

  const combinedDustbinStatus = {};
  if (dataForDetailView?.statusCounts) {
    const d1 = dataForDetailView.statusCounts.dustbin1Status || {};
    const d2 = dataForDetailView.statusCounts.dustbin2Status || {};
    Object.assign(combinedDustbinStatus, d1);
    for (const [key, value] of Object.entries(d2)) {
      combinedDustbinStatus[key] = (combinedDustbinStatus[key] || 0) + value;
    }
  }

  let kpiTitle = 'Total Feedbacks';
  let kpiValue = dashboardData?.overallStats?.totalFeedbacks || 0;

  if (selectedCoach && selectedCoachData) {
    kpiTitle = `Feedbacks for Coach ${selectedCoach}`;
    kpiValue = selectedCoachData.feedbackCount || 0;
  } else if (selectedTrain && selectedTrainData) {
    kpiTitle = `Feedbacks for ${selectedTrainData.trainName || 'N/A'}`;
    kpiValue = selectedTrainData.feedbackCount || 0;
  }

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-titles">
          <h1>RailHygiene</h1>
          <div className="last-updated">
            <RefreshCw size={14} className={`sync-icon ${isUpdating ? '' : 'stopped'}`} />
            <span>Live Sync • Last updated {
              dashboardData?.lastUpdated && typeof dashboardData.lastUpdated.toDate === 'function'
                ? dashboardData.lastUpdated.toDate().toLocaleString()
                : dashboardData?.lastUpdated
                  ? new Date(dashboardData.lastUpdated.seconds ? dashboardData.lastUpdated.seconds * 1000 : dashboardData.lastUpdated).toLocaleString()
                  : 'N/A'
            }</span>
          </div>
        </div>
      </header>

      <main className="dashboard-content">
        <div className="kpi-row">
          <div className="kpi-text-block">
            <h2>{kpiTitle}</h2>
            <p className="kpi-number">{kpiValue}</p>
          </div>
          
          <div className="filter-block">
            <h2>Filter by Train</h2>
            <Select
              className="train-filter"
              options={trainOptions}
              onChange={handleTrainChange}
              value={trainOptions.find(option => option.value === selectedTrain)}
              isClearable={true}
              placeholder="Search or select a train..."
            />
          </div>
          
          {selectedTrain && (
             <div className="filter-block">
              <h2>Filter by Coach</h2>
              <Select
                key={selectedTrain} 
                className="train-filter"
                options={coachOptions}
                onChange={handleCoachChange}
                value={coachOptions.find(option => option.value === selectedCoach)}
                isClearable={true}
                placeholder="All Coaches"
              />
            </div>
          )}
        </div>

        {selectedTrain && selectedTrainData ? (
          <div>
            <h2 className="view-title">
              {selectedTrainData.trainName} ({selectedTrain})
              {selectedCoach && ` — Coach ${selectedCoach}`}
            </h2>
            
            <div className="alerts-section" style={{ marginTop: '24px', marginBottom: '32px' }}>
              <div className="alert-item">
                <h3>Overall Avg Rating</h3>
                <p className="alert-number" style={{ color: 'var(--accent-color)' }}>
                  {dataForDetailView?.avgGeneralRating ? dataForDetailView.avgGeneralRating.toFixed(1) : 'N/A'}
                  <span style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>/5</span>
                </p>
              </div>
              <div className="alert-item">
                <h3>Water Not Available</h3>
                <p className="alert-number">
                  {(dataForDetailView?.booleanCounts?.toilet1WaterNA ?? 0) + (dataForDetailView?.booleanCounts?.toilet2WaterNA ?? 0)}
                </p>
              </div>
              <div className="alert-item">
                <h3>Trash in Toilet</h3>
                <p className="alert-number">
                   {(dataForDetailView?.booleanCounts?.toilet1Trash ?? 0) + (dataForDetailView?.booleanCounts?.toilet2Trash ?? 0)}
                </p>
              </div>
            </div>
            
            <div className="chart-grid">
              <div className="chart-section">
                <ComponentRatingChart trainData={dataForDetailView} />
              </div>
              <div className="chart-section">
                <FeedbackTrendChart feedbackByDate={dataForDetailView?.feedbacksByDate ?? {}} />
              </div>
              <div className="chart-section">
                <StatusDonutChart title="Floor Status" statusData={dataForDetailView?.statusCounts?.coachFloorStatus ?? {}} />
              </div>
              <div className="chart-section">
                <StatusDonutChart title="Toilet Status (Combined)" statusData={dataForDetailView?.statusCounts?.toilet1Status ?? {}} />
              </div>
              <div className="chart-section">
                <StatusDonutChart title="Dustbin Status (Combined)" statusData={combinedDustbinStatus} />
              </div>
            </div>
          </div>
        ) : (
          <div className="chart-grid">
            <div className="chart-section">
              <div className="chart-header">
                <h3 className="chart-title">Train Performance</h3>
                <div className="chart-controls">
                  <button onClick={() => setTrainChartMode('topRated')} className={trainChartMode === 'topRated' ? 'active' : ''}>Top Rated</button>
                  <button onClick={() => setTrainChartMode('bottomRated')} className={trainChartMode === 'bottomRated' ? 'active' : ''}>Lowest</button>
                  <button onClick={() => setTrainChartMode('mostFeedback')} className={trainChartMode === 'mostFeedback' ? 'active' : ''}>Volume</button>
                </div>
              </div>
              <TrainPerformanceChart 
                title=""
                chartData={topTrainsData} 
              />
            </div>
            
            <div className="chart-section">
              <div className="chart-header">
                <h3 className="chart-title">System-wide Volume</h3>
                <div className="chart-controls">
                  <button onClick={() => setDateRange('last7days')} className={dateRange === 'last7days' ? 'active' : ''}>7D</button>
                  <button onClick={() => setDateRange('last30days')} className={dateRange === 'last30days' ? 'active' : ''}>30D</button>
                  <button onClick={() => setDateRange('last90days')} className={dateRange === 'last90days' ? 'active' : ''}>90D</button>
                  <button onClick={() => setDateRange('allTime')} className={dateRange === 'allTime' ? 'active' : ''}>All</button>
                </div>
              </div>
              <FeedbackTrendChart feedbackByDate={filteredTrendData} />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
